"use client";

import { PlainMessage } from "@bufbuild/protobuf";
import {
  Price,
  Section,
  EventSeries,
} from "@egocentric-systems/ts-apis/booking_gateway/v1/events_pb";
import { faMinus } from "@fortawesome/free-solid-svg-icons";
import { faPlus } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { ChangeEvent, memo, useEffect, useMemo, useState } from "react";
import { Button } from "./ui/button";
import { cn, localTimeToDayjs } from "~/lib/utils";
import { useCart } from "~/hooks/useCart";
import { useToast } from "./ui/use-toast";
import dayjs from "dayjs";
import { useLocale } from "~/hooks/useLocale";
import { useTranslations } from "~/hooks/useTranslations";
import {
  DiscountConfig,
  SeatUpgrade,
} from "@egocentric-systems/ts-apis/booking/types/v1/order_pb";
import { EventOrBundle } from "~/lib/definitions";
import { useShopConfig } from "~/hooks/useShopConfig";
import { useNewItemIsInstantlyUpgraded } from "~/hooks/useNewItemIsInstantlyUpgraded";

type Props = {
  eventOrBundle: EventOrBundle;

  section: PlainMessage<Section>;
  price: PlainMessage<Price>;

  series: PlainMessage<EventSeries> | undefined;
  seat?: { id: number; row: string; name: string };
  discount?: PlainMessage<DiscountConfig>["reference"];
  availability: number | undefined;
  sumAmounts?: boolean;
  preorder?: {
    id: string;
    itemId: string;
  };
};

function parseText(text: string): number {
  if (text.length === 0) {
    return 0;
  }

  return parseInt(text, 10);
}

export const TicketControls = memo(function TicketControls(props: Props) {
  useLocale();
  const t = useTranslations();
  const { toast } = useToast();
  const {
    shopConfig: { fanTicket },
  } = useShopConfig();
  const {
    cancelSeat: cancel,
    setSeats: set,
    reserveSeat: reserve,
    uiItems,
  } = useCart();
  const isInstantlyUpgraded = useNewItemIsInstantlyUpgraded();
  const [focused, setFocused] = useState(false);
  const [saleDisabled, setSaleDisabled] = useState(false);

  useEffect(() => {
    const check = () => {
      const now = dayjs();
      const saleStart = localTimeToDayjs(
        props.eventOrBundle.value?.salesPeriod?.start,
      );
      const saleEnd = localTimeToDayjs(
        props.eventOrBundle.value?.salesPeriod?.end,
      );

      if (!now.isBetween(saleStart, saleEnd, "second")) {
        const { items, setItems, setUiItems } = useCart.getState();
        const newItems = items.filter(
          (item) =>
            item.eventOrBundle.case !== props.eventOrBundle.case ||
            item.eventOrBundle.value.id !== props.eventOrBundle.value?.id,
        );
        setItems(newItems);
        setUiItems(newItems);
        setSaleDisabled(true);
      } else {
        setSaleDisabled(false);
      }
    };

    check();

    const interval = setInterval(check, 1000);

    return () => {
      clearInterval(interval);
    };
  }, [props.eventOrBundle]);

  const handleIncrement = () => {
    if (!props.eventOrBundle.value || !props.series || props.seat !== undefined)
      return;

    reserve({
      price: props.price,
      eventOrBundle:
        props.eventOrBundle.case === "event"
          ? {
              case: "event",
              value: props.eventOrBundle.value,
            }
          : {
              case: "bundle",
              value: props.eventOrBundle.value,
            },
      series: props.series,
      section: props.section,
      seat: props.seat,
      upgrades: isInstantlyUpgraded(props.eventOrBundle)
        ? [SeatUpgrade.FANTICKET]
        : undefined,
      preorder: props.preorder,
    });
  };

  const handleDecrement = () => {
    if (!props.eventOrBundle.value || !props.series) return;

    cancel({
      price: props.price,
      eventOrBundle:
        props.eventOrBundle.case === "event"
          ? {
              case: "event",
              value: props.eventOrBundle.value,
            }
          : {
              case: "bundle",
              value: props.eventOrBundle.value,
            },
      series: props.series,
      section: props.section,
      seat: props.seat,
      discount: props.discount,
      preorder: props.preorder,
    });
  };

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (!props.eventOrBundle.value || !props.series) return;

    try {
      const value = parseText(event.target.value);

      if (value >= 0) {
        set({
          price: props.price,
          eventOrBundle:
            props.eventOrBundle.case === "event"
              ? {
                  case: "event",
                  value: props.eventOrBundle.value,
                }
              : {
                  case: "bundle",
                  value: props.eventOrBundle.value,
                },
          series: props.series,
          section: props.section,
          quantity: value,
          upgrades: isInstantlyUpgraded(props.eventOrBundle)
            ? [SeatUpgrade.FANTICKET]
            : undefined,
        });
      }
    } catch {
      toast({
        title: "Invalid quantity",
        description: t.ticket_add_error,
        variant: "destructive",
      });
    }
  };

  const quantity = useMemo(() => {
    if (props.sumAmounts) {
      const items = uiItems.filter(
        (item) =>
          item.series.id === props.series?.id &&
          item.eventOrBundle.case === props.eventOrBundle.case &&
          item.eventOrBundle.value.id === props.eventOrBundle.value?.id &&
          item.section.id === props.section.id &&
          item.price.id === props.price.id &&
          item.preorder?.id === props.preorder?.id &&
          item.preorder?.itemId === props.preorder?.itemId,
      );

      return items.reduce((total, item) => total + item.quantity, 0);
    }

    return (
      uiItems.find(
        (item) =>
          item.series.id === props.series?.id &&
          item.eventOrBundle.case === props.eventOrBundle.case &&
          item.eventOrBundle.value.id === props.eventOrBundle.value?.id &&
          item.section.id === props.section.id &&
          item.price.id === props.price.id &&
          item.seat?.id === props.seat?.id &&
          item.discount?.case === props.discount?.case &&
          item.discount?.value === props.discount?.value &&
          item.preorder?.id === props.preorder?.id &&
          item.preorder?.itemId === props.preorder?.itemId,
      )?.quantity ?? 0
    );
  }, [
    props.series?.id,
    props.eventOrBundle,
    props.section.id,
    props.price.id,
    props.seat?.id,
    props.discount,
    props.sumAmounts,
    props.preorder,
    uiItems,
  ]);

  const disabled = useMemo(() => {
    if (quantity >= props.section.capacity) return true;

    return props.availability !== undefined && props.availability === 0;
  }, [props.availability, props.section.capacity, quantity]);

  return (
    <div
      className={cn(
        "flex shrink-0 flex-row items-center rounded-md border border-solid border-gray-200",
        focused && "outline outline-2 outline-offset-2 outline-link",
      )}
    >
      <Button
        className="flex h-[38px] w-[45px] shrink-0 cursor-pointer items-center justify-center rounded-none rounded-bl rounded-tl bg-white p-0 outline-link transition-all disabled:bg-gray-100 disabled:opacity-100"
        type="button"
        variant="ghost"
        title={t.remove_ticket}
        aria-label={t.remove_ticket}
        onClick={handleDecrement}
        disabled={
          saleDisabled ||
          quantity === 0 ||
          Boolean(props.eventOrBundle.value?.soldOut)
        }
      >
        <FontAwesomeIcon
          aria-disabled={quantity === 0}
          className="text-lg font-bold text-link"
          icon={faMinus}
        />
      </Button>
      <input
        className="flex h-[38px] max-w-[120px] shrink-0 flex-row flex-nowrap items-center justify-center rounded-none bg-white px-3 py-0 text-center text-cart-input font-semibold leading-button text-text outline-none transition-all"
        type="text"
        inputMode="numeric"
        autoFocus={false}
        autoCorrect="off"
        autoComplete="off"
        autoCapitalize="off"
        autoSave="off"
        value={quantity}
        onChange={handleInputChange}
        disabled={
          saleDisabled ||
          Boolean(props.eventOrBundle.value?.soldOut) ||
          props.seat !== undefined ||
          props.preorder !== undefined
        }
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
      />
      <Button
        className="flex h-[38px] w-[45px] shrink-0 cursor-pointer items-center justify-center rounded-none rounded-br rounded-tr bg-white p-0 outline-link transition-all disabled:opacity-50"
        type="button"
        variant="ghost"
        title={t.add_ticket}
        aria-label={t.add_ticket}
        onClick={handleIncrement}
        disabled={
          disabled ||
          saleDisabled ||
          Boolean(props.eventOrBundle.value?.soldOut) ||
          props.seat !== undefined ||
          props.preorder !== undefined
        }
      >
        <FontAwesomeIcon
          aria-disabled={disabled}
          className="text-lg font-bold text-link"
          icon={faPlus}
        />
      </Button>
    </div>
  );
});
